import { Check, Monitor, Terminal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { secureOpenUrl } from '@/utils/linkValidation';

const VPSPricingSection = () => {
  const linuxVPS = [
    {
      name: "Basic Linux VPS",
      cpu: "1 vCPU",
      ram: "2GB RAM",
      port: "1Gbps port",
      price: "$5.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=3"
    },
    {
      name: "Standard Linux VPS",
      cpu: "2 vCPU",
      ram: "4GB RAM",
      port: "1Gbps port",
      price: "$10.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=4"
    },
    {
      name: "Advanced Linux VPS",
      cpu: "4 vCPU",
      ram: "6GB RAM",
      port: "1Gbps port",
      price: "$20.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=5"
    },
    {
      name: "XXL Linux VPS",
      cpu: "6 vCPU",
      ram: "8GB RAM",
      port: "1Gbps port",
      price: "$25.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=6"
    }
  ];

  const windowsVPS = [
    {
      name: "Basic Windows VPS",
      cpu: "1 vCPU",
      ram: "2GB RAM",
      port: "1Gbps port",
      price: "$10.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=7"
    },
    {
      name: "Standard Windows VPS",
      cpu: "2 vCPU",
      ram: "4GB RAM",
      port: "1Gbps port",
      price: "$20.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=8"
    },
    {
      name: "Advanced Windows VPS",
      cpu: "4 vCPU",
      ram: "6GB RAM",
      port: "1Gbps port",
      price: "$30.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=9"
    },
    {
      name: "XXL Windows VPS",
      cpu: "6 vCPU",
      ram: "8GB RAM",
      port: "1Gbps port",
      price: "$40.00",
      monthly: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=10"
    }
  ];

  const VPSCard = ({ vps, index }: { vps: any, index: number }) => (
    <Card key={vps.name} className="group relative border-gray-200 hover:shadow-2xl hover:shadow-nexx-green/30 hover:border-nexx-green transition-all duration-300 hover:scale-105 hover:z-50">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-bold text-center">{vps.name}</CardTitle>
        <CardDescription className="text-center">
          <span className="text-2xl font-bold text-nexx-green">{vps.price}</span>
          {vps.monthly && <span className="text-sm text-gray-500">/month</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {vps.cpu}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {vps.ram}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {vps.port}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            Full Root Access
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            24/7 Support
          </div>
        </div>
        
        <Button 
          className="w-full mt-6 bg-gray-900 hover:bg-gray-800 text-white transition-all duration-300 group-hover:shadow-lg group-hover:shadow-nexx-green/50 group-hover:bg-nexx-green"
          onClick={() => secureOpenUrl(vps.link)}
        >
          Order Now
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <section className="py-4 bg-gray-50 relative z-10 overflow-visible">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">VPS Solutions</h2>
          <p className="text-xl text-gray-600">
            Scalable virtual private servers for every need - the perfect{' '}
            <span className="relative inline-block text-nexx-green font-semibold">
              balance of performance, control, and affordability
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-nexx-green to-green-600 transform scale-x-0 animate-[scale-x-100_2s_ease-out_0.5s_forwards] origin-left"></span>
            </span>.
          </p>
        </div>

        {/* Linux VPS Section */}
        <div className="mb-6">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-r from-nexx-green to-green-600 rounded-2xl px-8 py-4 shadow-lg transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Terminal className="h-8 w-8 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white">Linux VPS</h3>
                  <p className="text-green-100 text-sm font-medium">Open Source • Flexible • Powerful</p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-20">
            {linuxVPS.map((vps, index) => (
              <VPSCard key={vps.name} vps={vps} index={index} />
            ))}
          </div>
        </div>

        {/* Windows VPS Section */}
        <div>
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl px-8 py-4 shadow-lg transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Monitor className="h-8 w-8 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white">Windows VPS</h3>
                  <p className="text-blue-100 text-sm font-medium">Enterprise • Compatible • Reliable</p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-20">
            {windowsVPS.map((vps, index) => (
              <VPSCard key={vps.name} vps={vps} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VPSPricingSection;
